/**
 * The Uploads tab in the Gallery and Strips looks. Same videos and same actions as the Classic
 * table (sort, combine into one sheet, open clips, Top 20, ⋮); only the way they're shown changes.
 *
 * One search: find a video by its name. (Search-what-was-said exists at /my-library/search/ but
 * has no button yet, until it finds videos well.) `?v=<id>` opens the quick look at one video,
 * `&at=<seconds>` at a line.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { VideoData } from "../types";
import GalleryView from "./GalleryView";
import StripsView from "./StripsView";
import QuickView from "./QuickView";
import { LibraryLook, useLibraryLook } from "./libraryLook";
import { FIRST_HINT, LOOK_NAMES, LOOK_TEXT, SORT_FILTER, TOOLBAR } from "./libraryLookText";
import { TOOLS_SLOT_ID } from "./LibraryShell";
import { VideoCardProps } from "./videoCardProps";

interface Props extends Omit<VideoCardProps, "onQuickLook"> {
  look: LibraryLook;
  allVideos: VideoData[];
  searchText: string;
  onSearchText: (text: string) => void;
  sortChoice: string;
  sortOptions: { key: string; label: string }[];
  onSortChoice: (key: string) => void;
  cardWidth: number;
  onCardWidth: (w: number) => void;
  onToggleCombine: () => void;
  onOpenCombined: () => void;
  onShare: (videoId: number) => void;
  onOpenBlackCherry: (video: VideoData) => void;
  onOpenExporter: (video: VideoData, clipIds: number[]) => void;
}

const PaperUploads: React.FC<Props> = (props) => {
  const {
    look, videos, allVideos, searchText, onSearchText, sortChoice, sortOptions, onSortChoice, cardWidth, onCardWidth,
    combineMode, picked, onToggleCombine, onOpenCombined, onOpenClips, onTop20, renderMenu,
  } = props;
  const [params, setParams] = useSearchParams();
  const { siteLook, canEdit, setLook, makeDefault } = useLibraryLook();
  // Sort and "From" (website / Premiere / phone app / link) share one small menu.
  const [sfOpen, setSfOpen] = useState(false);
  const [source, setSourceState] = useState<string>(() => {
    try { return localStorage.getItem("cc_library_source") || ""; } catch { return ""; }
  });
  const setSource = (next: string) => {
    setSourceState(next);
    try { localStorage.setItem("cc_library_source", next); } catch { /* ignore */ }
  };
  // One line that says what the three buttons do, until the person closes it.
  const [hintOpen, setHintOpen] = useState(() => {
    try { return localStorage.getItem("cc_library_hint_seen") !== "1"; } catch { return true; }
  });
  const closeHint = () => {
    setHintOpen(false);
    try { localStorage.setItem("cc_library_hint_seen", "1"); } catch { /* ignore */ }
  };
  // The tabs' line lives in the page frame (LibraryShell); find it once it's on the page.
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => { setSlot(document.getElementById(TOOLS_SLOT_ID)); }, []);

  const saveDefault = async () => {
    const name = LOOK_NAMES[look].name;
    if (!window.confirm(LOOK_TEXT.confirmDefault(name))) return;
    try {
      await makeDefault(look);
      window.alert(LOOK_TEXT.savedDefault(name));
    } catch (e) {
      window.alert((e as Error).message);
    }
  };

  const quickId = Number(params.get("v")) || null;
  const quickVideo = quickId ? allVideos.find((v) => v.id === quickId) : undefined;
  const at = params.get("at");
  const clipParam = params.get("c");

  // A quick look starts at the top of the page; going back returns to the same spot in the list.
  const listScroll = useRef(0);
  useEffect(() => {
    if (quickId) window.scrollTo({ top: 0 });
    else if (listScroll.current) window.scrollTo({ top: listScroll.current });
  }, [quickId]);

  const onQuickLook = (video: VideoData, start?: number, clipId?: number) => {
    listScroll.current = window.scrollY;
    const next: Record<string, string> = { v: String(video.id) };
    if (typeof start === "number") next.at = String(Math.floor(start));
    if (clipId) next.c = String(clipId);
    setParams(next);
  };

  if (quickVideo) {
    return (
      <QuickView
        video={quickVideo}
        startAt={at ? Number(at) : undefined}
        startClipId={clipParam ? Number(clipParam) : undefined}
        onBack={() => setParams({})}
        onOpenClips={onOpenClips}
        onTop20={onTop20}
        renderMenu={renderMenu}
        onShare={props.onShare}
        onOpenBlackCherry={props.onOpenBlackCherry}
        onOpenExporter={props.onOpenExporter}
        startArt={params.get("art") === "1"}
      />
    );
  }

  const shownVideos = source ? videos.filter((v) => (v.upload_source || "web") === source) : videos;
  const cardProps: VideoCardProps = { ...props, videos: shownVideos, onQuickLook };

  // Few tools, on the tabs' line: find a video, sort, the look, and ⋯ for the rest. The clips
  // are the page; everything else stays out of their way.
  const tools = (
    <>
      <button type="button" className={`chip${combineMode ? " on" : ""}`} title={TOOLBAR.combineTitle} onClick={onToggleCombine}>
        🗂 {combineMode ? TOOLBAR.stopCombining : TOOLBAR.combine}
      </button>
      <label className="find">
        <span aria-hidden>🔍</span>
        <input type="search" value={searchText} placeholder={TOOLBAR.findVideo} onChange={(e) => onSearchText(e.target.value)} />
      </label>
      <div className="more">
        <button type="button" className="sf" aria-expanded={sfOpen} onClick={() => setSfOpen((o) => !o)}>
          {sortOptions.find((o) => o.key === sortChoice)?.label}
          {source ? ` · ${SORT_FILTER.sources.find((x) => x.key === source)?.label}` : ""} ▾
        </button>
        {sfOpen && (
          <div className="pop sfpop" onMouseLeave={() => setSfOpen(false)}>
            <div className="note">{SORT_FILTER.sort}</div>
            <div className="sfchips">
              {sortOptions.map((o) => (
                <button type="button" key={o.key} className={`chip${sortChoice === o.key ? " on" : ""}`} onClick={() => onSortChoice(o.key)}>{o.label}</button>
              ))}
            </div>
            <div className="note">{SORT_FILTER.from}</div>
            <div className="sfchips">
              {SORT_FILTER.sources.map((o) => (
                <button type="button" key={o.key || "any"} className={`chip${source === o.key ? " on" : ""}`} onClick={() => setSource(o.key)}>{o.label}</button>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="ico" aria-label={LOOK_TEXT.label}>
        {(["gallery", "strips", "classic"] as LibraryLook[]).map((key) => (
          <button type="button" key={key} title={`${LOOK_NAMES[key].name}: ${LOOK_NAMES[key].title}`} className={look === key ? "on" : ""} onClick={() => setLook(key)}>
            {LOOK_NAMES[key].icon} {LOOK_NAMES[key].name}
          </button>
        ))}
      </div>
      {look === "gallery" && (
        <input type="range" min={220} max={460} step={10} value={cardWidth} aria-label={TOOLBAR.size} title={TOOLBAR.size}
          onChange={(e) => onCardWidth(Number(e.target.value))} style={{ width: 80, accentColor: "#e61d50" }} />
      )}
    </>
  );

  return (
    <div>
      {slot && createPortal(tools, slot)}
      {hintOpen && !combineMode && (
        <div className="hint1"><span>{FIRST_HINT.text}</span><button type="button" className="pill line" onClick={closeHint}>{FIRST_HINT.close}</button></div>
      )}
      {canEdit && look !== siteLook && (
        <div className="admin" style={{ marginBottom: 10 }}>
          {LOOK_TEXT.everyoneSees(LOOK_NAMES[siteLook].name)} · <button type="button" onClick={saveDefault}>{LOOK_TEXT.makeDefault}</button>
        </div>
      )}
      {combineMode && <div className="hint">{TOOLBAR.combining}</div>}
      {searchText.trim() && videos.length === 0 && <div className="meta">{TOOLBAR.noMatches}</div>}
      {source && videos.length > 0 && shownVideos.length === 0 && <div className="meta">{SORT_FILTER.noneFrom}</div>}
      {look === "strips" ? <StripsView {...cardProps} /> : <GalleryView {...cardProps} cardWidth={cardWidth} />}
      {combineMode && picked.size > 0 && (
        <div className="float">
          {TOOLBAR.picked(picked.size)}
          <button type="button" className="pill red" onClick={onOpenCombined}>{TOOLBAR.openCombined}</button>
        </div>
      )}
    </div>
  );
};

export default PaperUploads;
